/**
 * Subscription State Types
 *
 * Defines all possible subscription states and related types
 * for more granular subscription status tracking.
 */

/**
 * Possible subscription states in the application
 */
export type SubscriptionState =
  | "active" // Subscription is active and valid
  | "trial" // In trial period
  | "expired" // Subscription has expired
  | "grace_period" // Grace period after expiration
  | "cancelled" // User cancelled subscription
  | "past_due" // Payment failed, awaiting retry
  | "missing" // No subscription found
  | "loading" // Loading subscription data
  | "error"; // Error fetching subscription

/**
 * Subscription metadata for additional context
 */
export interface SubscriptionMetadata {
  /** When the subscription expires/expired */
  expiresAt?: Date;
  /** Days remaining until expiration (negative if expired) */
  daysRemaining?: number;
  /** Grace period end date */
  gracePeriodEndsAt?: Date;
  /** Trial end date */
  trialEndsAt?: Date;
  /** Is this a trial subscription? */
  isTrial?: boolean;
  /** Can user still access the app? */
  hasAccess: boolean;
  /** Should we show a warning? */
  shouldWarn: boolean;
  /** Should we block the user? */
  shouldBlock: boolean;
}

/**
 * Complete subscription status with state and metadata
 */
export interface SubscriptionStatus {
  state: SubscriptionState;
  metadata: SubscriptionMetadata;
  /** Human-readable status message */
  message?: string;
}

/**
 * Warning severity levels
 */
export type WarningSeverity = "info" | "warning" | "error" | "critical";

/**
 * Warning configuration
 */
export interface SubscriptionWarning {
  severity: WarningSeverity;
  title: string;
  message: string;
  /** Days until expiration */
  daysRemaining: number;
  /** Call to action text */
  ctaText: string;
  /** Should this warning be dismissible? */
  dismissible: boolean;
}

/**
 * Configuration for subscription warnings and grace periods
 */
export interface SubscriptionConfig {
  /** Days before expiration to start showing warnings */
  warningThresholdDays: number;
  /** Grace period days after expiration */
  gracePeriodDays: number;
  /** Show critical warning when this many days remain */
  criticalWarningDays: number;
}

/**
 * Default subscription configuration
 */
export const DEFAULT_SUBSCRIPTION_CONFIG: SubscriptionConfig = {
  warningThresholdDays: 7,
  gracePeriodDays: 3,
  criticalWarningDays: 2,
};

// =============================================================================
// SUBSCRIPTION FEATURES & LIMITS TYPES
// =============================================================================

/**
 * Subscription tier identifiers
 */
export type SubscriptionTier =
  | "free"
  | "essential"
  | "management"
  | "complete"
  | "basic"
  | "standard"
  | "advanced"
  | "unlimit"
  | "enterprise"
  | "personal_free"
  | "personal_basic"
  | "personal_partner";

/**
 * Feature keys for numeric limits (-1 means unlimited)
 */
export type NumericFeatureKey =
  | "maxEvents"
  | "maxMembers"
  | "maxActivities"
  | "maxInventoryItems"
  | "maxBankAccounts"
  | "maxIncomingTransactions"
  | "maxOutcomeTransactions"
  | "maxRitualBaths";

/**
 * Feature keys for boolean flags
 */
export type BooleanFeatureKey =
  | "advancedReports"
  | "customBranding"
  | "apiAccess"
  | "prioritySupport"
  | "dataExport"
  | "slackIntegration"
  | "canUseTemplates"
  | "canManageFinancials"
  | "canUseWhatsAppNotifications";

/**
 * All feature keys (numeric + boolean)
 */
export type FeatureKey = NumericFeatureKey | BooleanFeatureKey;

/**
 * Features available to a subscription tier
 */
export interface SubscriptionFeatures {
  // Numeric limits (-1 = unlimited)
  maxEvents: number;
  maxMembers: number;
  maxActivities: number;
  maxInventoryItems: number;
  maxBankAccounts: number;
  maxIncomingTransactions: number;
  maxOutcomeTransactions: number;
  maxRitualBaths: number;
  // Boolean features
  advancedReports: boolean;
  customBranding: boolean;
  apiAccess: boolean;
  prioritySupport: boolean;
  dataExport: boolean;
  slackIntegration: boolean;
  canUseTemplates: boolean;
  canManageFinancials: boolean;
  canUseWhatsAppNotifications: boolean;
}

/**
 * Single feature detail from the enhanced API response
 */
export interface FeatureDetail {
  feature: string;
  limit: number;
  used: number;
  remaining: number;
  unlimited: boolean;
  allowed: boolean;
}

/**
 * Enhanced subscription features response (from new backend API format)
 * Note: This is the EXPECTED format but not yet implemented by backend
 */
export interface EnhancedSubscriptionFeaturesResponse {
  context: "personal" | "terreiro";
  plan: string;
  planName: string;
  features: Record<string, FeatureDetail>;
  upgradeRecommended: boolean;
  nearingLimits: string[];
}

/**
 * Plan feature limits (numeric values, -1 = unlimited)
 */
export interface PlanFeatureLimits {
  maxEventsPerMonth: number;
  maxActivitiesPerMonth: number;
  maxInventoryItems: number;
  maxRitualBaths: number;
  maxMediums: number;
  maxIncomesPerMonth: number;
  maxOutcomesPerMonth: number;
  maxBankAccounts: number;
  maxStorageGB: number;
  canAddMediums: boolean;
  canManageFinancials: boolean;
  canUseAdvancedReports: boolean;
  canUseSlackIntegration: boolean;
  canUseWhatsAppNotifications: boolean;
  canExportData: boolean;
  canUseTemplates: boolean;
  prioritySupport: boolean;
}

/**
 * Plan pricing information
 */
export interface PlanPricing {
  monthly: number;
  yearly: number;
  monthlyFormatted: string;
  yearlyFormatted: string;
  yearlySavings: string;
  costPerMedium: number | null;
  costPerMediumFormatted: string | null;
}

/**
 * Subscription plan details
 */
export interface SubscriptionPlanDetails {
  tier: SubscriptionTier;
  name: string;
  description: string;
  features: PlanFeatureLimits;
  pricing: PlanPricing;
  monthlyPriceId?: string;
  yearlyPriceId?: string;
}

/**
 * ACTUAL API response format from /subscription/current-plan
 */
export interface CurrentPlanResponse {
  context?: "personal" | "terreiro";
  billingInterval?: "month" | "year" | null;
  currentPlan: SubscriptionPlanDetails;
  upgradeOptions: SubscriptionPlanDetails[];
  availablePlans?: SubscriptionPlanDetails[];
}

/**
 * Response from GET /subscription/features
 */
export interface SubscriptionFeaturesResponse {
  tier: SubscriptionTier;
  features: SubscriptionFeatures;
}

/**
 * Response from GET /subscription/check-limit
 */
export interface FeatureLimitCheckResponse {
  /** Whether the user can create more of this resource */
  allowed: boolean;
  /** Current count of this resource */
  currentCount: number;
  /** Maximum allowed (-1 = unlimited) */
  limit: number;
  /** Remaining allowed creations */
  remaining: number;
  /** Usage percentage (0-100) */
  percentage: number;
  /** True when usage >= 80% */
  nearingLimit: boolean;
  /** True when limit is reached */
  upgradeRequired: boolean;
  /** Human-readable message */
  message: string;
  /** Suggested tier to upgrade to (null if no upgrade needed) */
  suggestedUpgrade: SubscriptionTier | null;
}

/**
 * Single feature usage statistics
 */
export interface FeatureUsageStats {
  /** Current count of this resource */
  current: number;
  /** Maximum allowed (-1 = unlimited) */
  limit: number;
  /** Usage percentage (0-100) */
  percentage: number;
  /** True when usage >= 80% */
  nearingLimit: boolean;
}

/**
 * Usage stats keyed by feature name (API response format)
 */
export interface FeatureUsageMap {
  events: FeatureUsageStats;
  members: FeatureUsageStats;
  activities: FeatureUsageStats;
  inventoryItems: FeatureUsageStats;
  bankAccounts: FeatureUsageStats;
  incomingTransactions: FeatureUsageStats;
  outcomeTransactions: FeatureUsageStats;
  ritualBaths: FeatureUsageStats;
}

/**
 * Response from GET /subscription/usage
 */
export interface SubscriptionUsageResponse {
  tier: SubscriptionTier;
  usage: FeatureUsageMap;
}

/**
 * Limit exceeded error details (from 403 responses)
 */
export interface LimitExceededErrorDetails {
  feature: NumericFeatureKey;
  currentCount: number;
  limit: number;
  tier: SubscriptionTier;
  suggestedUpgrade: SubscriptionTier;
}

/**
 * Limit exceeded error response structure
 */
export interface LimitExceededError {
  error: string;
  code: "LIMIT_EXCEEDED";
  details: LimitExceededErrorDetails;
}

/**
 * Type guard to check if an error is a LimitExceededError
 */
export function isLimitExceededError(
  error: unknown,
): error is { response: { data: LimitExceededError } } {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: unknown }).response === "object" &&
    (error as { response: { data?: unknown } }).response?.data !== null
  ) {
    const data = (error as { response: { data: unknown } }).response.data;
    return (
      typeof data === "object" &&
      data !== null &&
      "code" in data &&
      (data as { code: string }).code === "LIMIT_EXCEEDED"
    );
  }
  return false;
}

/**
 * Mapping from NumericFeatureKey to usage key
 */
export const FEATURE_TO_USAGE_KEY: Record<
  NumericFeatureKey,
  keyof FeatureUsageMap
> = {
  maxEvents: "events",
  maxMembers: "members",
  maxActivities: "activities",
  maxInventoryItems: "inventoryItems",
  maxBankAccounts: "bankAccounts",
  maxIncomingTransactions: "incomingTransactions",
  maxOutcomeTransactions: "outcomeTransactions",
  maxRitualBaths: "ritualBaths",
};

/**
 * Human-readable labels for features (Portuguese)
 */
/**
 * Mapping from API feature names to NumericFeatureKey
 */
export const API_FEATURE_TO_KEY: Record<string, NumericFeatureKey> = {
  maxEventsPerMonth: "maxEvents",
  maxActivitiesPerMonth: "maxActivities",
  maxInventoryItems: "maxInventoryItems",
  maxRitualBaths: "maxRitualBaths",
  maxMediums: "maxMembers",
  maxIncomesPerMonth: "maxIncomingTransactions",
  maxOutcomesPerMonth: "maxOutcomeTransactions",
  maxBankAccounts: "maxBankAccounts",
};

/**
 * Mapping from NumericFeatureKey to API feature names
 * Used when sending requests to the backend
 */
export const KEY_TO_API_FEATURE: Record<NumericFeatureKey, string> = {
  maxEvents: "maxEventsPerMonth",
  maxActivities: "maxActivitiesPerMonth",
  maxInventoryItems: "maxInventoryItems",
  maxRitualBaths: "maxRitualBaths",
  maxMembers: "maxMediums",
  maxIncomingTransactions: "maxIncomesPerMonth",
  maxOutcomeTransactions: "maxOutcomesPerMonth",
  maxBankAccounts: "maxBankAccounts",
};

/**
 * Converts a frontend feature key to the API feature name
 * @param key - Frontend NumericFeatureKey
 * @returns API feature name string
 */
export function toApiFeatureName(key: NumericFeatureKey): string {
  return KEY_TO_API_FEATURE[key] || key;
}

/**
 * Converts an API feature name to the frontend key
 * @param apiName - API feature name string
 * @returns Frontend NumericFeatureKey or the original string if not found
 */
export function toFrontendFeatureKey(
  apiName: string,
): NumericFeatureKey | string {
  return API_FEATURE_TO_KEY[apiName] || apiName;
}

export const FEATURE_LABELS: Record<NumericFeatureKey, string> = {
  maxEvents: "Eventos",
  maxMembers: "Membros",
  maxActivities: "Atividades",
  maxInventoryItems: "Itens de Inventário",
  maxBankAccounts: "Contas bancárias (exceto caixa)",
  maxIncomingTransactions: "Transações de Contribuição",
  maxOutcomeTransactions: "Transações de Gastos",
  maxRitualBaths: "Banhos Rituais",
};

/**
 * Human-readable labels for boolean features (Portuguese)
 */
export const BOOLEAN_FEATURE_LABELS: Record<BooleanFeatureKey, string> = {
  advancedReports: "Relatórios Avançados",
  customBranding: "Marca Personalizada",
  apiAccess: "Acesso à API",
  prioritySupport: "Suporte Prioritário",
  dataExport: "Exportação de Dados",
  slackIntegration: "Integração Slack",
  canUseTemplates: "Modelos",
  canManageFinancials: "Gestão financeira",
  canUseWhatsAppNotifications: "Notificações WhatsApp",
};

/**
 * Tier display names (Portuguese)
 */
export const TIER_LABELS: Record<SubscriptionTier, string> = {
  free: "Grátis",
  essential: "Essencial",
  management: "Gestão",
  complete: "Completo",
  basic: "Essencial",
  standard: "Gestão",
  advanced: "Completo",
  unlimit: "Ilimitado",
  enterprise: "Ilimitado",
  personal_free: "Pessoal Gratuito",
  personal_basic: "Pessoal Básico",
  personal_partner: "Parceiro",
};
